import { createContext, useContext } from 'react';

import PropTypes from 'prop-types';

import { useRemoveContactsFromList } from '#contactListFeature/models/mutations';
import { useContactLists, useCampaignRecipients } from '#contactListFeature/models/queries';

import {
	useUpdateEmailCampaign,
	useUpsertEmailListsOfCampaign,
	useRemoveRecipientFromCampaign,
} from '../models/emailCampaigns/mutations';
import { useSingleEmailCampaign } from '../models/emailCampaigns/queries';

const EmailCampaignDetailsContext = createContext(null);

export const useEmailCampaignDetailsContext = () => {
	const context = useContext(EmailCampaignDetailsContext);
	if (!context) {
		throw new Error('useEmailCampaignDetailsContext must be used within EmailCampaignDetailsProvider');
	}
	return context;
};

export const EmailCampaignDetailsProvider = ({ children, campaignId }) => {
	// React Query hooks
	const { data: campaign, isLoading: loadingCampaign, refetch: refetchCampaign } = useSingleEmailCampaign(campaignId);

	const { data: contactLists = [], isLoading: loadingContactLists } = useContactLists();

	// Deduplicated recipients from backend
	const {
		data: flattenedLeads = [],
		isLoading: loadingLeads,
		refetch: refetchLeads,
	} = useCampaignRecipients(campaignId);

	// Mutations
	const updateCampaignMutation = useUpdateEmailCampaign();
	const upsertEmailListsMutation = useUpsertEmailListsOfCampaign();
	const removeContactFromListMutation = useRemoveContactsFromList();
	const removeRecipientMutation = useRemoveRecipientFromCampaign();

	// Action handlers
	const handleSaveCampaign = async ({
		name,
		status,
		selectedListIds,
		emailAccountId,
		sendingSchedule,
		maxEmailsPerDay,
		signatureId,
		showUnsubscribeLink,
	}) => {
		// Build updates object
		const updates = {
			campaignName: name,
			campaignStatus: status,
			emailAccountId: emailAccountId || null,
		};

		// Add optional fields if provided
		if (sendingSchedule !== undefined) {
			updates.sendingSchedule = sendingSchedule;
		}
		if (maxEmailsPerDay !== undefined) {
			updates.maxEmailsPerDay = maxEmailsPerDay;
		}
		if (signatureId !== undefined) {
			updates.signatureId = signatureId;
		}
		if (showUnsubscribeLink !== undefined) {
			updates.showUnsubscribeLink = showUnsubscribeLink;
		}

		// 1. Sync list assignments
		await upsertEmailListsMutation.mutateAsync({
			campaignId,
			listIds: selectedListIds,
		});

		// 2. Update campaign (a status change to 'active' triggers scheduling on the backend)
		await updateCampaignMutation.mutateAsync({
			id: campaignId,
			prevStatus: campaign.campaignStatus,
			updates,
		});

		// 3. Refetch data
		await refetchCampaign();
	};

	const handleRemoveContactFromList = async (contactId, listId) => {
		await removeContactFromListMutation.mutateAsync({ listId, scope: { ids: [contactId] } });

		// Refetch deduplicated recipients from backend
		await refetchLeads();
	};

	const handleRemoveRecipientFromCampaign = async (contactPersonId) => {
		await removeRecipientMutation.mutateAsync({
			campaignId,
			contactPersonId,
		});
	};

	const contextValue = {
		// Data
		campaign,
		contactLists,
		flattenedLeads,

		// Loading states
		loadingCampaign,
		loadingContactLists,
		loadingLeads,

		// Actions
		handleSaveCampaign,
		handleRemoveContactFromList,
		handleRemoveRecipientFromCampaign,

		// Refetch functions
		refetchCampaign,
		refetchLeads,
	};

	return <EmailCampaignDetailsContext.Provider value={contextValue}>{children}</EmailCampaignDetailsContext.Provider>;
};

EmailCampaignDetailsProvider.propTypes = {
	children: PropTypes.node.isRequired,
	campaignId: PropTypes.string.isRequired,
};
